// =======================================================
// GradientWipes — stacked moving gradient bands you can layer & color
// =======================================================
//
// Each layer is an angled gradient stripe (color A → color B, soft
// transparent edges) that wipes across the screen at its own speed
// and angle. Up to 4 layers stack with selectable blend modes, so a
// few quick palette tweaks produce wildly different clips.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMap;
  void main() {
    gl_FragColor = texture2D(uMap, vUv);
  }
`;

const BLEND_MODES = [
  ['source-over', 'Normal'],
  ['lighter',     'Add'],
  ['screen',      'Screen'],
  ['multiply',    'Multiply'],
  ['overlay',     'Overlay'],
  ['soft-light',  'Soft light'],
  ['hard-light',  'Hard light'],
  ['difference',  'Difference'],
];

const SHAPES = [
  ['stripe', 'Stripe (linear)'],
  ['radial', 'Radial pulse'],
  ['conic',  'Conic sweep'],
];

function hexToRgb(c) {
  if (c && typeof c === 'object') {
    return { r: Math.round(c.r ?? 255), g: Math.round(c.g ?? 255), b: Math.round(c.b ?? 255) };
  }
  const h = String(c || '#ffffff').replace('#', '');
  if (h.length >= 6) return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
  if (h.length >= 3) return {
    r: parseInt(h[0] + h[0], 16),
    g: parseInt(h[1] + h[1], 16),
    b: parseInt(h[2] + h[2], 16),
  };
  return { r: 255, g: 255, b: 255 };
}
function rgba(c, a) {
  const { r, g, b } = hexToRgb(c);
  return `rgba(${r},${g},${b},${a})`;
}

// Per-layer schema, prefixed l{N}_
function layerSchema(n, defaults) {
  return {
    [`l${n}_on`]:      { default: defaults.on,      type: 'bool',   label: `L${n} on` },
    [`l${n}_a`]:       { default: defaults.a,       type: 'color',  label: `L${n} color A` },
    [`l${n}_mid`]:     { default: defaults.mid,     type: 'color',  label: `L${n} mid` },
    [`l${n}_useMid`]:  { default: defaults.useMid,  type: 'bool',   label: `L${n} use mid` },
    [`l${n}_b`]:       { default: defaults.b,       type: 'color',  label: `L${n} color B` },
    [`l${n}_alphaEnd`]:{ default: defaults.alphaEnd,type: 'bool',   label: `L${n} fade end (B → transparent)` },
    [`l${n}_shape`]:   { default: defaults.shape,   type: 'select', label: `L${n} shape`,
                         options: SHAPES },
    [`l${n}_angle`]:   { default: defaults.angle,   min: -180, max: 180, step: 1,    label: `L${n} angle°` },
    [`l${n}_width`]:   { default: defaults.width,   min: 0.05, max: 3.0, step: 0.01, label: `L${n} width` },
    [`l${n}_speed`]:   { default: defaults.speed,   min: -2000, max: 2000, step: 5,  label: `L${n} speed (px/s)` },
    [`l${n}_softness`]:{ default: defaults.softness,min: 0,    max: 0.5, step: 0.01, label: `L${n} softness` },
    [`l${n}_opacity`]: { default: defaults.opacity, min: 0,    max: 1,   step: 0.01, label: `L${n} opacity` },
    [`l${n}_blend`]:   { default: defaults.blend,   type: 'select', label: `L${n} blend`,
                         options: BLEND_MODES },
  };
}

const DEFAULTS = [
  // L1 — directional spotlight wash like the moodboard: white → green → teal,
  //      fades into transparency on the dark side so it composites cleanly.
  { on: true,  a: '#ffffff', mid: '#9bff9c', b: '#1ee4c0', useMid: true,  alphaEnd: true,  shape: 'stripe', angle:  -45, width: 1.6, speed:   30, softness: 0.20, opacity: 1.00, blend: 'source-over' },
  { on: true,  a: '#ff4fd8', mid: '#7a1f5c', b: '#1a0030', useMid: false, alphaEnd: true,  shape: 'stripe', angle:  135, width: 1.4, speed:  -45, softness: 0.30, opacity: 0.75, blend: 'screen' },
  { on: false, a: '#ffd166', mid: '#ff5e78', b: '#7a1f5c', useMid: true,  alphaEnd: true,  shape: 'radial', angle:    0, width: 1.4, speed:   60, softness: 0.40, opacity: 0.55, blend: 'lighter' },
  { on: false, a: '#39f5ff', mid: '#ffffff', b: '#000000', useMid: false, alphaEnd: false, shape: 'conic',  angle:    0, width: 1.0, speed:   30, softness: 0.10, opacity: 0.30, blend: 'overlay' },
];

export class GradientWipes extends Generator {
  constructor() {
    super({
      name: 'Gradient Wipes',
      category: 'ambient',
      schema: {
        background: { default: '#080611', type: 'color', label: 'Background' },
        ...layerSchema(1, DEFAULTS[0]),
        ...layerSchema(2, DEFAULTS[1]),
        ...layerSchema(3, DEFAULTS[2]),
        ...layerSchema(4, DEFAULTS[3]),
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: false });
    this._resizeCanvas(engine.width, engine.height);

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uMap: { value: this.tex } },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);
  }

  onResize(w, h) { this._resizeCanvas(w, h); }

  _resizeCanvas(w, h) {
    const maxW = 4096;
    const maxH = 1080;
    this.off.width  = Math.max(2, Math.min(maxW, Math.round(w)));
    this.off.height = Math.max(2, Math.min(maxH, Math.round(h)));
    this._scale = this.off.width / Math.max(1, w);
  }

  update(dt, time, engine) {
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;

    // background
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = rgba(this.v('background'), 1);
    ctx.fillRect(0, 0, w, h);

    for (let n = 1; n <= 4; n++) {
      if (!this.v(`l${n}_on`)) continue;
      this._drawLayer(ctx, w, h, n, time);
    }

    // restore default state for next frame
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    this.tex.needsUpdate = true;
  }

  _drawLayer(ctx, w, h, n, time) {
    const shape = this.v(`l${n}_shape`);
    const a = this.v(`l${n}_a`);
    const mid = this.v(`l${n}_mid`);
    const useMid = !!this.v(`l${n}_useMid`);
    const b = this.v(`l${n}_b`);
    const fadeEnd = !!this.v(`l${n}_alphaEnd`);
    const ang = this.v(`l${n}_angle`) * Math.PI / 180;
    const widthFrac = this.v(`l${n}_width`);
    const speedPx = this.v(`l${n}_speed`) * this._scale;
    const soft = this.v(`l${n}_softness`);
    const op = this.v(`l${n}_opacity`);
    const blend = this.v(`l${n}_blend`);
    if (op <= 0) return;

    // Endpoint alpha — when fadeEnd is on, color B contributes no coverage,
    // so the layer fades to transparency on its B-side and lower layers /
    // background show through. Lets you stack washes like the moodboard.
    const aAlpha = 1;
    const bAlpha = fadeEnd ? 0 : 1;

    ctx.save();
    ctx.globalCompositeOperation = blend;
    ctx.globalAlpha = op;

    if (shape === 'stripe') {
      // Full-screen wipe: solid A on one side, transition band, solid B on
      // the other. The transition position scrolls along the angle axis.
      const diag = Math.hypot(w, h);
      // 'width' here is the gradient transition width (soft = transition length).
      // We extend the gradient line from -span..+span so solid A/B fill all the
      // off-axis area regardless of rotation.
      const span = diag;
      const transitionW = Math.max(2, widthFrac * diag * 0.5);
      // Position cycles across the full span so the wipe never stops.
      const period = span * 2 + transitionW;
      let pos = (time * speedPx) % period;
      if (pos < 0) pos += period;
      const center = -span + pos; // -span..+span

      ctx.translate(w / 2, h / 2);
      ctx.rotate(ang);

      const grad = ctx.createLinearGradient(-span, 0, span, 0);
      const t1 = (center - transitionW * 0.5 + span) / (2 * span);
      const t2 = (center + transitionW * 0.5 + span) / (2 * span);
      const tA = Math.max(0, Math.min(1, t1));
      const tB = Math.max(0, Math.min(1, t2));
      grad.addColorStop(0, rgba(a, aAlpha));
      if (tA > 0) grad.addColorStop(tA, rgba(a, aAlpha));
      if (useMid) {
        const tMid = (tA + tB) * 0.5;
        grad.addColorStop(tMid, rgba(mid, useMid ? (aAlpha + bAlpha) * 0.5 : aAlpha));
      }
      if (tB < 1) grad.addColorStop(tB, rgba(b, bAlpha));
      grad.addColorStop(1, rgba(b, bAlpha));
      ctx.fillStyle = grad;
      ctx.fillRect(-diag, -diag, diag * 2, diag * 2);
    } else if (shape === 'radial') {
      // Full-screen radial wipe: B at the very center expanding to A at the
      // edges. The "transition radius" pulses outward over time so the
      // colors swap places visually.
      const cx = w * 0.5;
      const cy = h * 0.5;
      const maxR = Math.hypot(w, h) * 0.6 * widthFrac;
      // pos scrolls 0..1 over time; we use it to move the transition radius
      const speedScale = speedPx / Math.max(200, maxR);
      const phase = 0.5 + 0.5 * Math.sin(time * speedScale);
      const transitionR = maxR * phase;
      const innerR = Math.max(1, transitionR * (1 - soft));
      const outerR = Math.max(innerR + 1, transitionR * (1 + soft));
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR + 1);
      // Center = B (or transparent when fadeEnd) → optional mid → edge = A
      grad.addColorStop(0, rgba(b, bAlpha));
      const tInner = Math.max(0.001, Math.min(0.999, innerR / (outerR + 1)));
      grad.addColorStop(tInner, rgba(b, bAlpha));
      if (useMid) {
        grad.addColorStop(Math.min(0.999, (tInner + 1) * 0.5), rgba(mid, (aAlpha + bAlpha) * 0.5));
      }
      grad.addColorStop(1, rgba(a, aAlpha));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      // also fill any pixel beyond the gradient with solid A (because radial
      // gradient extends outermost color, this is already the case — but on
      // some browsers we want the safety fill outside).
    } else if (shape === 'conic') {
      // Full-screen conic wipe — use native createConicGradient when present,
      // otherwise fall back to fan-of-wedges that fully cover the canvas.
      const cx = w * 0.5;
      const cy = h * 0.5;
      const r = Math.hypot(w, h);
      const rotation = ang + time * (speedPx / Math.max(200, r)) * 0.5;

      if (typeof ctx.createConicGradient === 'function') {
        const grad = ctx.createConicGradient(rotation, cx, cy);
        const sEdge = Math.min(0.24, soft * 0.5);
        if (useMid) {
          // A → mid → B → mid → A around the circle
          grad.addColorStop(0, rgba(a, aAlpha));
          grad.addColorStop(0.20 - sEdge, rgba(a, aAlpha));
          grad.addColorStop(0.30 + sEdge, rgba(mid, (aAlpha + bAlpha) * 0.5));
          grad.addColorStop(0.45 - sEdge, rgba(b, bAlpha));
          grad.addColorStop(0.55 + sEdge, rgba(b, bAlpha));
          grad.addColorStop(0.70 - sEdge, rgba(mid, (aAlpha + bAlpha) * 0.5));
          grad.addColorStop(0.80 + sEdge, rgba(a, aAlpha));
          grad.addColorStop(1, rgba(a, aAlpha));
        } else {
          grad.addColorStop(0, rgba(a, aAlpha));
          grad.addColorStop(0.25 - sEdge, rgba(a, aAlpha));
          grad.addColorStop(0.25 + sEdge, rgba(b, bAlpha));
          grad.addColorStop(0.75 - sEdge, rgba(b, bAlpha));
          grad.addColorStop(0.75 + sEdge, rgba(a, aAlpha));
          grad.addColorStop(1, rgba(a, aAlpha));
        }
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      } else {
        // fallback: fan of wedges, A→B→A→B around the circle
        const segments = 180;
        const aRgb = hexToRgb(a);
        const bRgb = hexToRgb(b);
        ctx.translate(cx, cy);
        ctx.rotate(rotation);
        for (let i = 0; i < segments; i++) {
          const t = i / segments;
          // triangular A↔B oscillation, two cycles per turn
          const tri = Math.abs(((t * 2) % 1) - 0.5) * 2; // 0..1
          const k = Math.pow(tri, 1 + soft * 4);
          const cr = Math.round(aRgb.r + (bRgb.r - aRgb.r) * k);
          const cg = Math.round(aRgb.g + (bRgb.g - aRgb.g) * k);
          const cb = Math.round(aRgb.b + (bRgb.b - aRgb.b) * k);
          ctx.fillStyle = `rgba(${cr},${cg},${cb},1)`;
          const a0 = (t - 0.5 / segments) * Math.PI * 2;
          const a1 = (t + 1.5 / segments) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.arc(0, 0, r, a0, a1);
          ctx.closePath();
          ctx.fill();
        }
      }
    }

    ctx.restore();
  }
}
